import { EventEmitter, Injectable } from '@angular/core';
import { Address, WrongCityWarning } from '@checkout/const';
import { DeliveryAddressFacade } from '@checkout/data-access';
import { BusinessRegionFacade } from '@city/data-access';
import { CityService } from '@city/feature';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DeliveryAddressService {
  addressList$ = this.deliveryAddressFacade.getAddressList();
  addressListLength$ = this.deliveryAddressFacade.getAddressListLength();
  activeAddressId$ = this.deliveryAddressFacade.getActiveAddressId();
  activeAddress$ = this.activeAddressId$.pipe<Address>(
    switchMap((id: string) => (id ? this.getAddress(id) : of(undefined))),
  );
  hasActiveAddress$ = this.activeAddress$.pipe<boolean>(map(Boolean));
  loading$ = this.deliveryAddressFacade.getLoading();
  success$ = this.deliveryAddressFacade.getSuccess();
  editableAddressId$ = new BehaviorSubject<string>(undefined);

  // Может так случится, что город в указанном в доставке адресе не совпадет
  // с городом в сторе. Бизнесс от этого ломается.
  // Поэтому пытаемся найти нессответсвие, сообщить и помочь пользователю с городами.
  addressCityMatches$: Observable<WrongCityWarning[]> = combineLatest([
    this.activeAddress$.pipe(
      distinctUntilChanged((prev: Address, next: Address) => {
        if (next && prev) {
          return prev.id === next.id;
        }
      }),
      tap((address) => {
        if (address) {
          this.businessRegionFacade.loadBusinessRegionForPoint(address.point);
        }
      }),
    ),
    this.cityService.activeBaseStore$,
    this.businessRegionFacade.getBusinessRegionForPoint(),
  ]).pipe(
    map(([address, baseStore, businessRegion]) => {
      if (!address || !businessRegion) {
        return [];
      }

      if (businessRegion.clientPoint.slug !== baseStore.code) {
        return [{ name: address.town || address.district, point: address.point }];
      }

      return [];
    }),
  );
  // эмитим во время сохранения адреса
  savedAddressEvent = new EventEmitter();
  selectActiveAddressIdEvent = new EventEmitter<string>();

  constructor(
    private deliveryAddressFacade: DeliveryAddressFacade,
    private cityService: CityService,
    private businessRegionFacade: BusinessRegionFacade,
  ) {}

  getAddress(id: string): Observable<Address> {
    return this.deliveryAddressFacade.getAddress(id);
  }

  closeAddressModal(): void {
    this.savedAddressEvent.emit();
  }

  selectActiveAddressId(id: string): void {
    this.deliveryAddressFacade.selectActiveAddressId(id);
    this.selectActiveAddressIdEvent.emit(id);
  }

  saveAddress(address: Address): void {
    this.deliveryAddressFacade.saveAddress(address);
  }

  editSavedAddress(address: Address): void {
    this.deliveryAddressFacade.editSavedAddress(address);
  }

  deleteSavedAddress(id: string): void {
    this.deliveryAddressFacade.deleteSavedAddress(id);
  }
}
